import React from 'react';
import { motion } from 'framer-motion';
import { highlightTerms } from '../highlightTerms';
import { ChevronRight } from 'lucide-react';

export const TitleSlide = ({ slide }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <div
      className="relative h-full w-full flex items-center justify-center overflow-hidden"
      data-testid={`slide-${slide.id}`}
    >
      {/* Background Image with Overlay */}
      {slide.image && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#17072F] via-[#17072F]/80 to-[#17072F]/60" />
        </>
      )}

      {/* Content */}
      <motion.div
        className="relative z-10 text-center px-8 md:px-16 max-w-6xl"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h1
          variants={itemVariants}
          className="pres-font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tight text-white leading-none drop-shadow-2xl"
        >
          {highlightTerms(slide.title)}
        </motion.h1>
        
        {slide.subtitle && (
          <motion.p
            variants={itemVariants}
            className="font-sans text-xl md:text-2xl lg:text-3xl text-white italic mt-6 tracking-wide"
          >
            {slide.subtitle}
          </motion.p>
        )}

        {slide.bullets && (
          <motion.div variants={itemVariants} className="mt-12 space-y-4">
            {slide.bullets.map((bullet, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="flex items-center justify-center gap-3 text-xl md:text-2xl text-gray-200"
              >
                <ChevronRight className="w-6 h-6 text-[#D31113]" />
                <span>{highlightTerms(bullet)}</span>
              </motion.div>
            ))}
          </motion.div>
        )}
      </motion.div>

      {/* Author credit at bottom */}
      {slide.author && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 font-sans text-base md:text-lg text-white/70 tracking-wide z-20"
        >
          {slide.author}
        </motion.p>
      )}

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#17072F] to-transparent" />
    </div>
  );
};
